import { useState } from 'react';
import { Copy, Check, ChevronDown, ChevronUp } from 'lucide-react';

export default function ContentExport() {
  const [copiedSection, setCopiedSection] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  const copyToClipboard = (text, sectionId) => {
    navigator.clipboard.writeText(text);
    setCopiedSection(sectionId);
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const ContentBlock = ({ id, title, content }) => (
    <div className="border border-gray-200 rounded-lg mb-4 bg-white">
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
        onClick={() => toggleSection(id)}
      >
        <h3 className="font-bold text-lg text-gray-800">{title}</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              copyToClipboard(content, id);
            }}
            className="flex items-center gap-1 px-3 py-1 bg-teal-600 text-white rounded-full text-sm hover:bg-teal-700 transition-colors"
          >
            {copiedSection === id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copiedSection === id ? 'Copied!' : 'Copy'}
          </button>
          {expandedSections[id] ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
        </div>
      </div>
      {expandedSections[id] && (
        <div className="p-4 pt-0 border-t border-gray-100">
          <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans bg-gray-50 p-4 rounded-lg overflow-x-auto">
            {content}
          </pre>
        </div>
      )}
    </div>
  );

  const sections = [
    {
      id: 'homepage-hero',
      title: 'Homepage - Hero Section',
      content: `HEADLINE:
Transform Your Grief Into Renewed Purpose

SUBHEADLINE:
Dr. Angelia Griffin guides high-functioning leaders and communities through loss with clarity, movement, and meaning—turning pain into purpose.

CTA BUTTONS:
- Explore Programs
- Meet Dr. Griffin`
    },
    {
      id: 'homepage-mission',
      title: 'Homepage - Mission Statement',
      content: `"After the tragic loss of her son, William Donald Paul II, Dr. Griffin transformed personal grief into a life's mission—combining over 30 years of leadership experience with lived experience to guide others through their darkest moments."`
    },
    {
      id: 'homepage-services',
      title: 'Homepage - Programs Overview',
      content: `SECTION TITLE: The M.O.V.E.™ Course System
HEADLINE: One Framework, Tailored to Your Loss
DESCRIPTION: From a single introductory workshop to the flagship cohort, choose the structured path that meets you exactly where you are.

M.O.V.E.™ FOUNDATIONS INTRODUCTION — $27
90-minute live workshop. Your first step into structured grief transformation.

M.O.V.E.™ FOUNDATIONS COURSE — $197
Self-paced, 18 lessons, lifetime access. Complete the full framework on your own schedule.

M.O.V.E.™ WORKSHOP SERIES — $747
4-week live series. Four weeks of live, structured grief transformation.

M.O.V.E.™ INTENSIVE EXPERIENCE — $297 per person
Full-day intensive (in-person or virtual). The complete framework in one immersive day.

M.O.V.E.™ COHORT EXPERIENCE — $1,497
Flagship 8-week program. Live transformation, community & deep reintegration.`
    },
    {
      id: 'about-bio',
      title: 'About Page - Full Bio',
      content: `Dr. Angelia Griffin is a Grief Coach, Educator, and Keynote Speaker who helps high-functioning leaders and communities transform loss into clarity, movement, and renewed purpose.

After the tragic loss of her son, William Donald Paul II, Dr. Griffin transformed personal grief into a life's mission—founding IGMA, Inc., a 501(c)(3) nonprofit, and AGC LLC Grief Coaching.

With over 30 years of leadership experience and more than 20 years in higher education, Dr. Griffin combines academic rigor with lived experience to guide individuals through acute, delayed, and hidden grief using a movement- and meaning-based process.

Her approach isn't about forgetting or "moving on"—it's about integrating loss into a new chapter of life filled with purpose and meaning. Like the Japanese art of Kintsugi, where broken pottery is repaired with gold, Dr. Griffin helps her clients find the gold in their brokenness.

Dr. Griffin is the author of "Turning In Is The Only Way Out" and host of the weekly talk show "All That Matters."`
    },
    {
      id: 'about-credentials',
      title: 'About Page - Credentials',
      content: `CREDENTIALS:
• 30+ Years Leadership - Decades of experience guiding teams and organizations through transformation.
• 20+ Years in Education - Deep expertise in higher education and adult learning methodologies.
• Keynote Speaker - Sought-after speaker on grief, resilience, and purpose-driven leadership.
• Lived Experience - Personal journey through grief transformed into mission to help others.

ORGANIZATIONS:

IGMA, Inc. (501c3 Nonprofit)
"I Give Myself Away"
A 501(c)(3) nonprofit organization committed to empowering others through training and community awareness initiatives.

AGC LLC Grief Coaching
Professional Grief Coaching Services
Professional grief coaching services for individuals, groups, and organizations.`
    },
    {
      id: 'move-framework',
      title: 'M.O.V.E.™ Framework - Modules & Pathways',
      content: `THE M.O.V.E.™ COURSE SYSTEM
One Framework, Tailored to Your Loss

THE FOUR MODULES (THE ENGINE — NEVER CHANGES):
1. Regulation & Safety — Stabilize the nervous system, establish internal and relational safety, build capacity for deeper grief work.
2. Identity & Meaning — Explore how loss disrupted your sense of self; begin reconstructing meaning and purpose.
3. Voice & Boundaries — Articulate your grief, set limits in relationships, communicate your needs with clarity and confidence.
4. Strategic Re-Engagement — Move forward with intention, rebuild life structures, integrate the loss into a renewed identity and direction.

THE FOUR GRIEF PATHWAYS (TAILORED CUSTOMIZATION):
• Loss of a Loved One
• Loss of a Pet
• Breakup / Divorce
• Loss of Career / Identity`
    },
    {
      id: 'program-foundations-introduction',
      title: 'Program - Foundations Introduction ($27)',
      content: `M.O.V.E.™ FOUNDATIONS INTRODUCTION — $27
Your first step into structured grief transformation.
Format: 90-minute live workshop (virtual)

INCLUDES:
• 90 minutes of live facilitation with Dr. Angelia Griffin
• M.O.V.E.™ Overview Guide — digital download
• Grief Pathway self-assessment (Loved One · Divorce · Pet · Career)
• Access to next-step program details and early-enrollment pricing

CTA: Register Now`
    },
    {
      id: 'program-foundations-course',
      title: 'Program - Foundations Course ($197)',
      content: `M.O.V.E.™ FOUNDATIONS COURSE — $197
Complete the full framework on your own schedule.
Format: Self-paced · 18 lessons · 4 modules · Lifetime access

INCLUDES:
• 18 structured video lessons covering all four M.O.V.E.™ modules
• Pathway selection at enrollment — content tailors to your type of loss
• Complete M.O.V.E.™ Participant Workbook with reflection prompts
• Module assessments to track your growth and progress
• Downloadable resources and exercises for each lesson
• Lifetime access — revisit any module any time, on any device

CTA: Enroll Now`
    },
    {
      id: 'program-workshop-series',
      title: 'Program - Workshop Series ($747)',
      content: `M.O.V.E.™ WORKSHOP SERIES — $747
Four weeks of live, structured grief transformation.
Format: 4 weeks · 90 min/session · Live facilitation

INCLUDES:
• 4 live 90-minute facilitated sessions with Dr. Angelia Griffin
• M.O.V.E.™ Participant Workbook — digital
• Weekly reflection exercises and integration prompts
• Group discussion and peer community access during the series
• Completion certificate upon finishing all four sessions
• Pathway-specific language for your type of loss

NOTE: Graduates receive a credit toward the M.O.V.E.™ Cohort Experience.
CTA: Reserve Your Spot`
    },
    {
      id: 'program-intensive',
      title: 'Program - Intensive Experience ($297)',
      content: `M.O.V.E.™ INTENSIVE EXPERIENCE — $297 per person
The complete framework in one powerful immersive day.
Format: Full-day workshop · In-person or virtual

INCLUDES:
• Full-day live facilitation — all four M.O.V.E.™ modules in one session
• M.O.V.E.™ Participant Workbook (physical for in-person, digital for virtual)
• M.O.V.E.™ Assessment — before and after to measure your movement
• Graduation certificate and completion documentation

WHO IT'S FOR:
• Corporate & organizational wellness days and team offsites
• Retreat centers, faith communities, and hospital programs
• Individuals who want the full framework in a single immersive day

NOTE: Group & organizational rates available.
CTA: Book the Intensive`
    },
    {
      id: 'program-cohort',
      title: 'Program - Cohort Experience ($1,497)',
      content: `M.O.V.E.™ COHORT EXPERIENCE — $1,497 (Flagship Program)
Eight weeks of live transformation, community & deep reintegration.
Format: 8 weeks · Weekly 90-min live sessions

INCLUDES:
• Full M.O.V.E.™ curriculum — all 4 modules
• 8 live 90-minute sessions with Dr. Griffin
• Digital workbook & M.O.V.E.™ assessment
• Private cohort community access
• Weekly accountability & implementation support
• Pathway-specific breakout discussions
• Graduation certificate & recognition

NOTE: Enrollment opens quarterly. Payment plans available.
CTA: Apply for the Next Cohort`
    },
    {
      id: 'speaking-topics',
      title: 'Speaking - Topics',
      content: `KEYNOTE SPEAKING

SPEAKING TOPICS:

Turning Pain Into Purpose
How to transform personal tragedy into meaningful work and renewed direction.

Leading Through Loss
Strategies for high-functioning leaders navigating grief while maintaining performance.

Building Grief-Competent Organizations
Creating cultures that support employees through loss without productivity collapse.

The Language of Loss
What to say, what not to say, and how to create space for healing conversations.

Movement & Meaning After Loss
Practical frameworks for finding forward motion when grief feels paralyzing.

Custom Topics
Tailored presentations for your specific audience, industry, or organizational needs.

EVENT TYPES:
• Corporate conferences & retreats
• Healthcare & hospital staff events
• Church & ministry gatherings
• Nonprofit leadership summits
• Community awareness events
• Professional development workshops
• Academic conferences
• Memorial & remembrance services`
    },
    {
      id: 'contact-info',
      title: 'Contact Information',
      content: `CONTACT INFORMATION:

Email: info@drangeliagriffin.com
Phone: (770) 422-4048
Location: Atlanta Metro Area
Virtual sessions available worldwide

OFFICE HOURS:
Monday – Friday: 9:00 AM – 5:00 PM EST
Weekend sessions available by appointment

DISCLAIMER:
Grief coaching is not therapy or crisis intervention. If you are in immediate crisis, please contact a mental health professional or call the 988 Suicide & Crisis Lifeline.`
    },
    {
      id: 'brand-colors',
      title: 'Brand Colors & Fonts',
      content: `BRAND COLORS:

Primary Teal: #2D7D7B
Teal Light: #4A9D9B
Teal Dark: #1F5A58

Gold: #C9A962
Gold Light: #E0C585
Gold Dark: #A0853D

Navy: #1B365D
Navy Light: #2C4A7A

Background Sand: #F5F5F0
Background Bone: #FAFAF8

FONTS:
Headings: Playfair Display (Serif)
Body Text: Manrope (Sans-serif)

TAGLINE:
"Turning Pain Into Purpose"`
    }
  ];

  const copyAll = () => {
    const allContent = sections.map(s => `=== ${s.title.toUpperCase()} ===\n\n${s.content}`).join('\n\n\n');
    copyToClipboard(allContent, 'all');
  };

  return (
    <div className="min-h-screen bg-gray-100 py-12" data-testid="content-export-page">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Website Content Export</h1>
          <p className="text-lg text-gray-600 mb-6">
            Click any section to expand, then copy the content for use in Go HighLevel or other platforms.
          </p>
          <button
            onClick={copyAll}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-full font-bold hover:bg-gray-800 transition-colors"
          >
            {copiedSection === 'all' ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            {copiedSection === 'all' ? 'All Content Copied!' : 'Copy All Content'}
          </button>
        </div>

        <div className="space-y-2">
          {sections.map(section => (
            <ContentBlock
              key={section.id}
              id={section.id}
              title={section.title}
              content={section.content}
            />
          ))}
        </div>

        <div className="mt-12 text-center">
          <a 
            href="/"
            className="text-teal-600 hover:text-teal-700 font-medium"
          >
            ← Back to Website
          </a>
        </div>
      </div>
    </div>
  );
}
